import React from "react";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";

const SEO = ({ title, description, image }) => {
    const { i18n } = useTranslation();
    const lang = i18n.resolvedLanguage;
    const isEnglish = lang === 'en';

    const location = useLocation();
    const pathname = location.pathname;
    const baseUrl = window.location.origin;
    const canonicalUrl = `${baseUrl}${pathname}`;

    const cleanPath = pathname.endsWith('/') && pathname !== '/' ? pathname.slice(0, -1) : pathname;
    const alternateLang = isEnglish ? 'pt' : 'en';

    const mainDescriptionEnglish = "Find Bitcoin ATMs and buy Bitcoin, Litecoin and other crypto instantly in Portugal with cash and card.";
    const mainDescriptionPortuguese = "Encontre Bitcoin ATMs e compre Bitcoin, Litecoin e outras criptomoedas instantaneamente em Portugal com numerário ou com cartão multibanco.";

    const sanitizedPath = (() => {
        const cleaned = pathname
            .replace(/^\/|\/$/g, '')               // strip leading/trailing slashes
            .toLowerCase();

        // Handle root paths like "/en" or "/pt" → treat as "home"
        if (!cleaned || /^(en|pt)$/.test(cleaned)) {
            return 'mindthecoin_com_home';
        }

        return 'mindthecoin_com_' + cleaned
            .replace(/\//g, '_')                   // replace slashes with underscores
            .replace(/[^a-z0-9_]/gi, '');          // clean non-alphanumeric
    })();

    const metaImage = `${baseUrl}/assets/images/screenshots/${sanitizedPath}.jpg`;


    const descriptions = {
        "/": isEnglish ? mainDescriptionEnglish : mainDescriptionPortuguese,
        "/en/": mainDescriptionEnglish,
        "/pt/": mainDescriptionPortuguese,
        "/en/faq": "Frequently Asked Questions about Bitcoin and crypto purchases.",
        "/en/instructions": "Learn how to buy Bitcoin using Bitcoin ATMs from Mind the Coin.",
        "/en/informations": "Learn more about Cryptocurrencies with Mind the Coin.",
        "/pt/faq": "Perguntas frequentes sobre Bitcoin e compras de criptomoedas.",
        "/pt/instrucoes": "Aprenda a comprar Bitcoin usando as Bitcoin ATMs da Mind the Coin.",
        "/pt/informacoes": "Saiba mais sobre Criptomoedas com a Mind the Coin.",
    };

    const alternatePaths = {
        "/": isEnglish ? "/en" : "/pt",
        "/en": "/pt",
        "/pt": "/en",
        "/en/informations": "/pt/informacoes",
        "/en/instructions": "/pt/instrucoes",
        "/en/faq": "/pt/faq",
        "/en/privacy": "/pt/privacidade",
        "/en/terms": "/pt/termos",
        "/en/cookies": "/pt/cookies",
        "/pt/informacoes": "/en/informations",
        "/pt/instrucoes": "/en/instructions",
        "/pt/faq": "/en/faq",
        "/pt/privacidade": "/en/privacy",
        "/pt/termos": "/en/terms",
        "/pt/cookies": "/en/cookies",
    };

    const metaTitle = title || (isEnglish ? "Mind the Coin - Buy Bitcoin in Portugal" : "Mind the Coin - Comprar Bitcoin em Portugal em tempo real!");
    const metaDescription = description || descriptions[pathname] || descriptions["/"];

    return (
        <Helmet>
            <html lang={lang} />
            <title>{metaTitle}</title>
            <meta name="description" content={metaDescription} />
            <link rel="canonical" href={canonicalUrl} />

            {/* Open Graph */}
            <meta property="og:type" content="website" />
            <meta property="og:title" content={metaTitle} />
            <meta property="og:description" content={metaDescription} />
            <meta property="og:url" content={canonicalUrl} />
            <meta property="og:image" content={metaImage} />

            {/* Twitter Card */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={metaTitle} />
            <meta name="twitter:description" content={metaDescription} />
            <meta name="twitter:image" content={metaImage} />

            {/* Language alternates */}
            <link rel="alternate" hrefLang="x-default" href={`${baseUrl}/`} />
            <link rel="alternate" hrefLang={alternateLang} href={`${baseUrl}${alternatePaths[cleanPath] || ''}`} />
            <link rel="alternate" hrefLang={lang} href={`${baseUrl}${cleanPath}`} />

            {/* Favicon */}
            <link rel="icon" href={`${baseUrl}/assets/images/favicon.png`} type="image/png" />

            {/* Schema.org Organization JSON-LD */}
            <script type="application/ld+json">
                {JSON.stringify({
                    "@context": "https://schema.org",
                    "@type": "Organization",
                    "name": "Mind the Coin",
                    "url": "https://mindthecoin.com",
                    "logo": `${baseUrl}/assets/images/logo.png`,
                    "description": "Encontre e utilize Bitcoin ATMs em Portugal. Compre Bitcoin, Litecoin e outras criptomoedas em tempo real com dinheiro ou cartão multibanco.",
                    "foundingDate": "2018",
                    "sameAs": [
                        "https://www.facebook.com/mindthecoin",
                        "https://x.com/mindthecoin",
                        "https://www.instagram.com/mindthecoin"
                    ]
                })}
            </script>
        </Helmet>
    );
};

export default SEO;